import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Paper, TextField, MenuItem, Select, InputLabel, FormControl,
  Box, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';

const Test = () => {
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [followUpError, setFollowUpError] = useState('');
  const [newLead, setNewLead] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phoneNumber: '',
    helpMessage: '',
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await fetch('https://api.prim-u.com/api/organizations');
        const data = await res.json();
        setLeads(data);
      } catch (err) {
        setError('Failed to fetch leads.');
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

const handleFollowUp = async (email) => {
  try {
    const response = await fetch('https://api.prim-u.com/api/follow-up-emails', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) throw new Error('Failed to send follow-up email');

    const updatedLeads = leads.map((lead) =>
      lead.email === email ? { ...lead } : lead
    );
    setLeads(updatedLeads);
    setFollowUpMessage(`✅ Follow-up email sent successfully to ${email}`);
    setFollowUpError('');
  } catch (error) {
    console.error(error);
    setFollowUpError(`❌ Failed to send follow-up email to ${email}`);
    setFollowUpMessage('');
  }
};



  const handleInputChange = (e) => {
    setNewLead({ ...newLead, [e.target.name]: e.target.value });
  };

  const handleAddNewLead = async () => {
    if (!newLead.firstName || !newLead.lastName || !newLead.email || !newLead.companyName) {
      setFormError('Please fill in all required fields.');
      return;
    }

    try {
      // Get reCAPTCHA token
      const token = await window.grecaptcha.execute('6LfpAy8rAAAAAFc799DuZbo4FldHk9CirWzsbKUs', { action: 'submit' });

      const payload = {
        ...newLead,
        recaptchaToken: token,
      };

      const response = await fetch('https://api.prim-u.com/api/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log(result)
      if (!response.ok) {
        setFormError(`Error: ${result.body.error || 'Unknown error'}`);
        return;
      }

      // Add to list
      setLeads([result, ...leads]);
      setOpenForm(false);
      setNewLead({ firstName: '', lastName: '', phoneNumber: '', email: '', companyName: '', helpMessage: '' });
      setFormError('');
    } catch (error) {
      setFormError('Network error. Please try again later.');
    }
  };



  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      (lead.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (lead.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? lead.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });

  const paginatedLeads = filteredLeads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div style={{ backgroundColor: '#f9f4fe', minHeight: '100vh', padding: '20px' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '20px' }}>Admin Dashboard</h1>

      {followUpMessage && (
        <p style={{ color: 'green', textAlign: 'center' }}>{followUpMessage}</p>
      )}
      {followUpError && (
        <p style={{ color: 'red', textAlign: 'center' }}>{followUpError}</p>
      )}


      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="20px">
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '200px' }}
        />

        <FormControl style={{ width: '200px' }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status Filter"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="New">New</MenuItem>
            <MenuItem value="QUALIFICATION & SCHEDULING">QUALIFICATION & SCHEDULING</MenuItem>
            <MenuItem value="Closed">Closed</MenuItem>
          </Select>
        </FormControl>

        <Button variant="contained" color="secondary" onClick={() => setOpenForm(true)}>
          Add New Lead
        </Button>
      </Box>

      <Dialog
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setFormError('');
        }}
      >
        <DialogTitle>Add New Lead</DialogTitle>
        <DialogContent>
          {formError && (
            <p style={{ color: 'red', marginBottom: '10px' }}>{formError}</p>
          )}
          <TextField name="firstName" label="Name" fullWidth margin="dense" value={newLead.firstName} onChange={handleInputChange} required />
          <TextField name="lastName" label="Surname" fullWidth margin="dense" value={newLead.lastName} onChange={handleInputChange} required />
          <TextField name="companyName" label="Company" fullWidth margin="dense" value={newLead.companyName} onChange={handleInputChange} required />
          <TextField name="email" label="Email" fullWidth margin="dense" value={newLead.email} onChange={handleInputChange} required />
          <TextField name="phoneNumber" label="Phone Number" fullWidth margin="dense" value={newLead.phoneNumber} onChange={handleInputChange} />
          <TextField name="helpMessage" label="Message" fullWidth margin="dense" multiline rows={3} value={newLead.helpMessage} onChange={handleInputChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNewLead}>Submit</Button>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Message</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLeads.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell>{lead.firstName} {lead.lastName}</TableCell>
                <TableCell>{lead.email}</TableCell>
                <TableCell>{lead.phoneNumber}</TableCell>
                <TableCell>{lead.status}</TableCell>
                <TableCell>{lead.helpMessage}</TableCell>
                <TableCell>{formatDate(lead.timestamp || lead.timestamp)}</TableCell>
                <TableCell>
                  <Button variant="contained" color="primary" style={{ marginRight: 8 }} onClick={() => handleFollowUp(lead.email)}>
                    Follow-Up
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {paginatedLeads.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No results found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredLeads.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>
    </div>
  );
};

export default Test;
